import { useState } from "react";
import { LayoutTemplate, Play, Pencil, Trash2, Check, X, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";

export interface TruckTemplate {
  id: string;
  name: string;
  origin: string;
  destination: string;
  equipment_type: string;
  length_ft: number;
  max_weight_lbs: number;
  load_type: string;
  open_to_anywhere: boolean;
  usage_count: number;
  last_used_at: string | null;
}

interface Props {
  templates: TruckTemplate[];
  onUse: (t: TruckTemplate) => void;
  onRename: (id: string, newName: string) => void;
  onDelete: (id: string) => void;
}

export default function TruckTemplatesSection({ templates, onUse, onRename, onDelete }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [deletingId, setDeletingId] = useState<string | null>(null);

  if (templates.length === 0) return null;

  const startRename = (t: TruckTemplate) => {
    setEditingId(t.id);
    setEditName(t.name);
  };

  const confirmRename = () => {
    if (editingId && editName.trim()) {
      onRename(editingId, editName.trim());
    }
    setEditingId(null);
  };

  const confirmDelete = (id: string) => {
    onDelete(id);
    setDeletingId(null);
  };

  const formatSpecs = (t: TruckTemplate) => {
    const parts: string[] = [];
    if (t.equipment_type) parts.push(t.equipment_type.replace(/_/g, " "));
    if (t.length_ft) parts.push(`${t.length_ft} ft`);
    if (t.load_type) parts.push(t.load_type);
    return parts.join(" • ") || "—";
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="rounded-xl border border-border bg-card" style={{ boxShadow: "var(--shadow-card)" }}>
        <CollapsibleTrigger asChild>
          <button className="flex items-center gap-2 px-4 py-2.5 w-full text-left hover:bg-muted/50 transition-colors rounded-xl">
            <LayoutTemplate className="h-3.5 w-3.5 shrink-0" style={{ color: "hsl(var(--brand-teal-deep))" }} />
            <h4 className="text-[10px] font-bold uppercase tracking-wider text-foreground">Truck Templates</h4>
            <span className="ml-1 inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-muted text-[10px] font-semibold text-muted-foreground">
              {templates.length}
            </span>
            <ChevronDown
              className="h-3.5 w-3.5 ml-auto text-muted-foreground transition-transform duration-200"
              style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}
            />
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="max-h-[240px] overflow-y-auto border-t border-border divide-y divide-border">
            {templates.map((t) => (
              <div key={t.id} className="px-4 py-2.5 flex items-center gap-3 group">
                <div className="flex-1 min-w-0">
                  {editingId === t.id ? (
                    <div className="flex items-center gap-1.5">
                      <Input
                        className="h-7 text-xs flex-1"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") confirmRename();
                          if (e.key === "Escape") setEditingId(null);
                        }}
                        autoFocus
                        maxLength={100}
                      />
                      <button onClick={confirmRename} className="text-foreground hover:text-primary p-0.5">
                        <Check className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => setEditingId(null)} className="text-muted-foreground hover:text-foreground p-0.5">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <p className="text-xs font-semibold text-foreground truncate">{t.name}</p>
                      <p className="text-[10px] text-muted-foreground capitalize">{formatSpecs(t)}</p>
                    </>
                  )}
                </div>

                {editingId !== t.id && (
                  <div className="flex items-center gap-1 shrink-0">
                    {deletingId === t.id ? (
                      <>
                        <span className="text-[10px] text-muted-foreground mr-1">Delete?</span>
                        <button
                          onClick={() => confirmDelete(t.id)}
                          className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
                          style={{ color: "hsl(var(--danger))" }}
                        >
                          Yes
                        </button>
                        <button
                          onClick={() => setDeletingId(null)}
                          className="text-[10px] font-semibold text-muted-foreground px-1.5 py-0.5 rounded"
                        >
                          No
                        </button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => startRename(t)}
                        >
                          <Pencil className="h-3 w-3 text-muted-foreground" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => setDeletingId(t.id)}
                        >
                          <Trash2 className="h-3 w-3 text-muted-foreground" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 text-[10px] font-bold uppercase tracking-wider px-2.5 ml-1"
                          onClick={() => onUse(t)}
                        >
                          <Play className="h-2.5 w-2.5 mr-1" />
                          Use
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}
